<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  connector: {
    type: Object,
    default: null,
  },
  connectorTypes: {
    type: Object,
    default: () => ({}),
  },
  selectedType: {
    type: String,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();

const formData = ref({
  name: '',
  connector_type: '',
  api_url: '',
  api_token: '',
  api_user: '',
  api_password: '',
  is_active: true,
});

const isEditing = computed(() => !!props.connector);

const selectedTypeInfo = computed(() => {
  if (!formData.value.connector_type) return null;
  return props.connectorTypes[formData.value.connector_type];
});

const namePlaceholder = computed(() => {
  if (selectedTypeInfo.value?.name) {
    return `ex: ${selectedTypeInfo.value.name} - Produção`;
  }
  return t('AI_AGENTS.CONNECTORS.FORM.NAME_PLACEHOLDER');
});

const needsCredentials = computed(() => {
  // Some connectors use user+password instead of token
  return ['mk_auth', 'sgp'].includes(formData.value.connector_type);
});

const isValid = computed(() => {
  if (
    !formData.value.name ||
    !formData.value.connector_type ||
    !formData.value.api_url
  ) {
    return false;
  }
  // Ao editar, não exige token/senha (mantém os valores atuais)
  if (isEditing.value) {
    return true;
  }
  // Ao criar, exige credenciais
  if (needsCredentials.value) {
    return formData.value.api_user && formData.value.api_password;
  }
  return formData.value.api_token;
});

const handleSubmit = () => {
  if (!isValid.value) return;

  // Preparar dados, removendo campos vazios de credenciais ao editar
  const data = { ...formData.value };

  // Não enviar token/senha vazios (para manter os valores atuais)
  if (!data.api_token) delete data.api_token;
  if (!data.api_password) delete data.api_password;

  emit('save', data);
};

watch(
  [() => props.connector, () => props.selectedType],
  ([newConnector, newType]) => {
    if (newConnector) {
      formData.value = {
        name: newConnector.name || '',
        connector_type: newConnector.connector_type || '',
        api_url: newConnector.api_url || '',
        api_token: '',
        api_user: newConnector.api_user || '',
        api_password: '',
        is_active: newConnector.is_active ?? true,
      };
    } else {
      formData.value = {
        name: '',
        connector_type: newType || '',
        api_url: '',
        api_token: '',
        api_user: '',
        api_password: '',
        is_active: true,
      };
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="w-full">
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <!-- Selected Type Header (when type is pre-selected) -->
      <div
        v-if="selectedTypeInfo"
        class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
      >
        <div class="p-2 rounded-lg bg-white border border-slate-200">
          <span class="i-lucide-plug text-lg text-slate-600" />
        </div>
        <div>
          <p class="text-sm font-medium text-slate-800">
            {{ selectedTypeInfo.name }}
          </p>
          <p class="text-xs text-slate-500">
            {{ selectedTypeInfo.description }}
          </p>
        </div>
      </div>

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.NAME') }}
        </label>
        <input
          v-model="formData.name"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          :placeholder="namePlaceholder"
        />
      </div>

      <!-- API URL -->
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.API_URL') }}
        </label>
        <input
          v-model="formData.api_url"
          type="url"
          class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          :placeholder="t('AI_AGENTS.CONNECTORS.FORM.API_URL_PLACEHOLDER')"
        />
      </div>

      <!-- Token (for token-based auth) -->
      <div v-if="!needsCredentials">
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.TOKEN') }}
        </label>
        <input
          v-model="formData.api_token"
          type="password"
          class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          :placeholder="
            isEditing
              ? t('AI_AGENTS.CONNECTORS.FORM.TOKEN_PLACEHOLDER_EDIT')
              : t('AI_AGENTS.CONNECTORS.FORM.TOKEN_PLACEHOLDER')
          "
        />
        <p v-if="isEditing" class="mt-1 text-xs text-slate-500">
          {{ t('AI_AGENTS.CONNECTORS.FORM.TOKEN_HINT') }}
        </p>
      </div>

      <!-- User & Password (for credential-based auth) -->
      <template v-else>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('AI_AGENTS.CONNECTORS.FORM.USER') }}
          </label>
          <input
            v-model="formData.api_user"
            type="text"
            class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            :placeholder="t('AI_AGENTS.CONNECTORS.FORM.USER_PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('AI_AGENTS.CONNECTORS.FORM.PASSWORD') }}
          </label>
          <input
            v-model="formData.api_password"
            type="password"
            class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            :placeholder="
              isEditing
                ? t('AI_AGENTS.CONNECTORS.FORM.PASSWORD_PLACEHOLDER_EDIT')
                : t('AI_AGENTS.CONNECTORS.FORM.PASSWORD_PLACEHOLDER')
            "
          />
        </div>
      </template>

      <!-- Available Tools Info (compact) -->
      <div
        v-if="selectedTypeInfo?.tools?.length"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span class="text-xs text-slate-500">{{
          t('AI_AGENTS.CONNECTORS.TOOLS_LABEL')
        }}</span>
        <span
          v-for="tool in selectedTypeInfo.tools"
          :key="tool"
          class="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded"
        >
          {{ tool.replace(/_/g, ' ') }}
        </span>
      </div>

      <!-- Active -->
      <div class="flex items-center gap-2">
        <input
          id="is_active"
          v-model="formData.is_active"
          type="checkbox"
          class="w-4 h-4 rounded border-slate-300 text-woot-600 focus:ring-woot-500"
        />
        <label for="is_active" class="text-sm text-slate-700">
          {{ t('AI_AGENTS.CONNECTORS.FORM.ACTIVE') }}
        </label>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button
          :label="t('AI_AGENTS.CONNECTORS.FORM.CANCEL')"
          variant="faded"
          color="slate"
          @click="emit('cancel')"
        />
        <Button
          :label="
            isEditing
              ? t('AI_AGENTS.CONNECTORS.FORM.UPDATE')
              : t('AI_AGENTS.CONNECTORS.FORM.CREATE')
          "
          :is-loading="isSaving"
          :disabled="!isValid"
          @click="handleSubmit"
        />
      </div>
    </form>
  </div>
</template>
